import { normalizeColor } from './CompanionDecoder';
import { CompanionActionState, CompanionTheme, CustomCtaCreative } from './CompanionTypes';

const FALLBACK_SURFACE = '#FFFFFF';
const FALLBACK_TEXT = '#111111';
const FALLBACK_BUTTON = '#1A73E8';

export interface ResolvedColors {
  surface: string;
  text: string;
  button: string;
  buttonText: string;
}

function luminance(hex: string): number {
  const channel = (offset: number): number => {
    const value = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

function blackOrWhite(background: string): string {
  return contrastRatio('#000000', background) >= contrastRatio('#FFFFFF', background) ? '#000000' : '#FFFFFF';
}

/** Keeps text readable: below 4.5:1 contrast it falls back to black or white. */
export function readable(foreground: string | undefined, background: string): string {
  if (foreground && contrastRatio(foreground, background) >= 4.5) {
    return foreground;
  }
  return blackOrWhite(background);
}

/** Creative-supplied colors win over the app theme, which wins over the built-in fallbacks. */
export function resolveColors(theme: CompanionTheme, creative: Partial<ResolvedColors> = {}): ResolvedColors {
  const surface = normalizeColor(creative.surface) ?? normalizeColor(theme.surfaceColor) ?? FALLBACK_SURFACE;
  const button = normalizeColor(creative.button) ?? normalizeColor(theme.buttonColor) ?? FALLBACK_BUTTON;
  return {
    surface,
    text: readable(normalizeColor(creative.text) ?? normalizeColor(theme.textColor) ?? FALLBACK_TEXT, surface),
    button,
    buttonText: readable(normalizeColor(creative.buttonText) ?? normalizeColor(theme.buttonTextColor), button),
  };
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  styles: Partial<CSSStyleDeclaration> | undefined,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  Object.assign(node.style, styles ?? {});
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

function image(url: string, alt: string, styles: Partial<CSSStyleDeclaration>): HTMLImageElement {
  const img = el('img', styles);
  img.alt = alt;
  img.src = url;
  return img;
}

function button(label: string, colors: ResolvedColors, onClick: () => void): HTMLButtonElement {
  const btn = el('button', {
    background: colors.button,
    color: colors.buttonText,
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    font: 'inherit',
    fontWeight: '600',
  });
  btn.type = 'button';
  btn.textContent = label;
  btn.addEventListener('click', onClick);
  return btn;
}

/** Compact row with icon, copy and one action button. Copy is set as text, never as HTML. */
export class CustomCtaView {
  readonly element: HTMLElement;
  private readonly button_: HTMLButtonElement;
  private readonly defaultLabel_: string;
  private disposed_ = false;

  constructor(
    creative: CustomCtaCreative,
    colors: ResolvedColors,
    onAction: () => void,
    onIconFailed: (url: string) => void,
  ) {
    this.element = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '8px 12px',
      boxSizing: 'border-box',
      width: '100%',
      background: colors.surface,
      color: colors.text,
    });
    this.element.dataset.companion = 'customCta';

    const icon = image(creative.iconUrl, '', { width: '40px', height: '40px', objectFit: 'contain' });
    icon.addEventListener('error', () => {
      if (!this.disposed_) onIconFailed(creative.iconUrl);
    });
    const copy = el('div', { flex: '1', minWidth: '0' });
    copy.append(el('div', { fontWeight: '700' }, creative.title));
    if (creative.description) {
      copy.append(el('div', { fontSize: '0.875em' }, creative.description));
    }
    this.defaultLabel_ = creative.ctaText;
    this.button_ = button(creative.ctaText, colors, () => {
      if (!this.disposed_) onAction();
    });
    this.element.append(icon, copy, this.button_);
  }

  setActionState(state: CompanionActionState): void {
    this.button_.textContent = state.label ?? this.defaultLabel_;
    this.button_.disabled = state.disabled ?? false;
    this.button_.setAttribute('aria-busy', String(state.disabled ?? false));
  }

  dispose(): void {
    this.disposed_ = true;
    this.button_.disabled = true;
    this.element.remove();
  }
}
